import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AxiosInstance from "../Home/AxiosInstance";
import { clearAuthSession } from "../Home/auth";
import Profile from "../Home/Profile.png";
import logo from "../Home/logo.png";
import "bootstrap/dist/css/bootstrap.min.css";
import "./TrainerHeader.css";

const BASE_PATH = (process.env.REACT_APP_BASE_PATH || "/als").replace(/\/+$/, "");

export default function TrainerHeader() {
  const [trainerId, setTrainerId] = useState(null);
  const [trainerDetails, setTrainerDetails] = useState(null);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isNotificationModalOpen, setNotificationModalOpen] = useState(false);

  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const baseURL = AxiosInstance.defaults.baseURL;
  const BASE_MEDIA_URL = `${baseURL}media/`;

  const profileImageSrc = trainerDetails?.image_path
    ? `${BASE_MEDIA_URL}${trainerDetails.image_path}`
    : Profile;

  const isProfilePage = location.pathname === "/profile";
  const fullName = trainerDetails
    ? `${trainerDetails.first_name || ""} ${
        trainerDetails.last_name || ""
      }`.trim()
    : "";

  useEffect(() => {
    const storedTrainerId = localStorage.getItem("trainerId");
    if (storedTrainerId) {
      setTrainerId(storedTrainerId);
      fetchTrainerDetails(storedTrainerId);
      fetchNotifications(storedTrainerId);
    }
  }, []);

  const fetchTrainerDetails = async (id) => {
    try {
      const response = await AxiosInstance.get(
        `companies/trainer-details/${id}`
      );
      setTrainerDetails(response.data);
    } catch (error) {
      console.error("Error fetching trainer details", error);
    }
  };

  const fetchNotifications = async (id) => {
    try {
      const res = await AxiosInstance.get(`companies/notification/${id}/`);
      const inAppNotifications = res.data.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setNotifications(inAppNotifications);
    } catch (err) {
      console.error("Error fetching notifications:", err);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await AxiosInstance.delete(`companies/notifications/${notificationId}`);
      setNotifications((prev) =>
        prev.filter((n) => n.notification_id !== notificationId)
      );
    } catch (error) {
      console.error("Error deleting notification:", error);
      alert("Failed to delete the notification. Please try again.");
    }
  };

  const toggleDropdown = () => {
    setDropdownVisible((prev) => !prev);
    setNotificationModalOpen(false);
  };

  const openNotificationModal = () => {
    setNotificationModalOpen(true);
    setDropdownVisible(false);
  };

  const closeNotificationModal = () => {
    setNotificationModalOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        !dropdownRef.current?.contains(e.target) &&
        !notificationRef.current?.contains(e.target)
      ) {
        setDropdownVisible(false);
      }
      if (
        isNotificationModalOpen &&
        notificationRef.current &&
        !notificationRef.current.contains(e.target)
      ) {
        closeNotificationModal();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isNotificationModalOpen]);

  const handleDetails = () => {
    navigate("/trainer-details");
    setDropdownVisible(false);
  };

  const handleProfiles = () => {
    navigate("/my-profiles");
    setDropdownVisible(false);
  };

  const handleChangePassword = () => {
    navigate("/change-password", {
      state: {
        userType: "trainer",
        userId: trainerId,
      },
    });
    setDropdownVisible(false);
  };

  const handleSignOut = () => {
    clearAuthSession();
    window.location.href = `${BASE_PATH}/trainers`;
    setDropdownVisible(false);
  };

  return (
    <header className="trainer-header">
      <div className="trainer-header-inner">
        <div className="trainer-brand">
          <img src={logo} alt="Open Learnings" className="trainer-brand-logo" />
          <span className="trainer-brand-text">
            <strong>Open Learnings</strong>
            <small>Trainer portal</small>
          </span>
        </div>

        {!isProfilePage && trainerDetails && (
          <div className="trainer-header-user" ref={dropdownRef}>
            <button
              type="button"
              className="trainer-user-btn"
              onClick={toggleDropdown}
              aria-expanded={dropdownVisible}
            >
              <span className="trainer-user-meta">
                <strong>{fullName || "Trainer"}</strong>
                <small>Trainer</small>
              </span>
              <img
                src={profileImageSrc}
                alt=""
                className="trainer-profile-image"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = Profile;
                }}
              />
            </button>

            {dropdownVisible && (
              <div className="trainer-dropdown">
                <button type="button" onClick={handleDetails}>
                  My Details
                </button>
                <button type="button" onClick={handleProfiles}>
                  My Profiles
                </button>
                <button type="button" onClick={openNotificationModal}>
                  Notifications
                  {notifications.length > 0 && (
                    <span className="trainer-notif-count">
                      {notifications.length}
                    </span>
                  )}
                </button>
                <button type="button" onClick={handleChangePassword}>
                  Change Password
                </button>
                <button
                  type="button"
                  className="trainer-signout"
                  onClick={handleSignOut}
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {isNotificationModalOpen && (
        <>
          <div
            className="trainer-notif-backdrop"
            onClick={closeNotificationModal}
          />
          <aside className="trainer-notif-panel" ref={notificationRef}>
            <div className="trainer-notif-head">
              <h5>Notifications</h5>
              <button
                type="button"
                onClick={closeNotificationModal}
                aria-label="Close notifications"
              >
                ×
              </button>
            </div>
            {notifications.length === 0 ? (
              <p className="trainer-notif-empty">No notifications yet</p>
            ) : (
              <ul>
                {notifications.map((n) => (
                  <li key={n.notification_id}>
                    <span>{n.message}</span>
                    <button
                      type="button"
                      onClick={() =>
                        handleDeleteNotification(n.notification_id)
                      }
                      aria-label="Delete notification"
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        </>
      )}
    </header>
  );
}
